import { motion } from "framer-motion";
import { useEffect, useState } from "react";

// Counter animation for the numbers
function CounterNumber({ target, duration }) {
  const [count, setCount] = useState(0);
  const animDuration = duration || 2.5;

  useEffect(() => {
    let startTime = null;
    let animationFrame = null;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / (animDuration * 1000), 1);

      setCount(Math.floor(progress * target));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [target, animDuration]);

  return <span>{count}</span>;
}

export default function Stats({ stats }) {
  return (
    <>
      {stats.map((stat, index) => {
        const Icon = stat.icon;

        return (
          <motion.div
            key={index}
            className={`relative group cursor-pointer ${stat.order}`}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: 0.3 + index * 0.08,
              ease: [0.25, 1, 0.5, 1],
            }}
            whileHover={{
              y: -8,
              scale: 1.03,
              transition: { duration: 0.22, ease: "easeOut" },
            }}
          >
            <div
              className={`relative rounded-none py-4 px-3 text-center
                h-full flex flex-col justify-between
                ${
                  stat.color === "blue"
                    ? "bg-[#006699] shadow-lg shadow-[#006699]/20"
                    : "bg-white shadow-lg shadow-gray-200/50"
                }
                transition-all duration-300 ease-out`}
            >
              {/* L-Shape Top Left */}
              <div 
                className={`absolute top-0 left-0 w-6 h-6 border-t-[3px] border-l-[3px] ${
                  stat.color === "blue" ? "border-white/50" : "border-[#006699]/50"
                }`} 
              />
              {/* L-Shape Bottom Right */}
              <div 
                className={`absolute bottom-0 right-0 w-6 h-6 border-b-[3px] border-r-[3px] ${
                  stat.color === "blue" ? "border-white/50" : "border-[#006699]/50"
                }`} 
              />

              {/* Grain overlay for pro texture */}
              <div
                className="absolute inset-0 opacity-[0.25] mix-blend-overlay pointer-events-none"
                style={{
                  backgroundImage:
                    "url('https://grainy-gradients.vercel.app/noise.svg')",
                }}
              />

              <div className="relative z-10">
                <div className="flex justify-center mb-2">
                  <div
                    className={`p-2.5 rounded-none ${
                      stat.color === "blue" 
                        ? "bg-white/10" 
                        : "bg-[#006699]/10"
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 md:w-6 md:h-6 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6 ${
                        stat.color === "blue" ? "text-white" : "text-[#006699]"
                      }`}
                    />
                  </div>
                </div>

                <p
                  className={`text-3xl md:text-4xl font-bold mb-0.5 tracking-tight ${
                    stat.color === "blue" ? "text-white" : "text-[#006699]"
                  }`}
                >
                  <CounterNumber target={stat.number} duration={2.5} />+
                </p>

                <p
                  className={`text-[10px] md:text-xs uppercase tracking-wider font-bold mt-1 ${
                    stat.color === "blue" ? "text-white/90" : "text-gray-800"
                  }`}
                >
                  {stat.label}
                </p>
              </div>

              <p
                className={`text-[10px] md:text-xs mt-2 font-medium relative z-10 ${
                  stat.color === "blue" ? "text-white/70" : "text-gray-500"
                }`}
              >
                {stat.description}
              </p>
            </div>
          </motion.div>
        );
      })}
    </>
  );
}
